import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import { AxiosError } from 'axios';
import { showNotification } from '../../slices/notification.slice';
import {
  IGetAdminGuardiansResponse,
  IGetAdminGuardiansParams,
  IGetAdminGuardianChildrenResponse,
  IUnlinkGuardianChildResponse,
  ICreateAdminGuardianPayload,
  ICreateAdminGuardianResponse,
  IGuardianOrganizationOption,
  IGetAdminGuardianByIdResponse,
  IGetAdminGuardianInstitutesResponse,
  IDeactivateGuardianPayload,
  IDeactivateGuardianResponse,
  IUnlinkGuardianFromInstitutePayload,
  IUnlinkGuardianFromInstituteResponse,
  IUpdateAdminGuardianPayload,
  IUpdateAdminGuardianResponse,
} from '../../../interface/classveew-admin/admin-guardian.interface';
import {
  IGuardianSearchResult,
  IManualGuardianCreationPayload,
} from '../../../interface/guardian.interface';
import { IErrorResponse, IApiErrorResponse } from '../../../interface/main.interface';
import {
  getAdminGuardians,
  getAdminGuardianById,
  getAdminGuardianInstitutes,
  getAdminGuardianChildren,
  deactivateAdminGuardian,
  reactivateAdminGuardian,
  deleteAdminGuardian,
  unlinkGuardianFromInstitute,
  unlinkAdminGuardianChild,
  updateAdminGuardian,
  createAdminGuardian as createAdminGuardianService,
  adminOrganizationService,
} from '../../../services/classveew-admin';
import {
  searchGuardianByEmail,
  adminLinkGuardian,
  sendGuardianInvite,
  manualCreateGuardian,
} from '../../../services/classveew-admin/guardian';

/**
 * Fetch all admin guardians with optional query parameters
 */
export const fetchAdminGuardians = createAsyncThunk<
  IGetAdminGuardiansResponse,
  IGetAdminGuardiansParams,
  { rejectValue: IErrorResponse }
>('admin-guardian/fetch-all', async (params, { rejectWithValue }) => {
  try {
    const response = await getAdminGuardians(params);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to fetch guardians.',
    });
  }
});

/**
 * Search for a guardian by email
 */
export const searchGuardian = createAsyncThunk<
  IGuardianSearchResult,
  { email: string; studentId?: string },
  { rejectValue: IErrorResponse }
>('adminGuardian/searchGuardian', async (params, { rejectWithValue }) => {
  try {
    const response = await searchGuardianByEmail(params.email, params.studentId);
    return response;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to search guardian',
    });
  }
});

/**
 * Fetch a single admin guardian by ID
 */
export const fetchAdminGuardianById = createAsyncThunk<
  IGetAdminGuardianByIdResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-guardian/fetch-by-id', async (guardianId, { rejectWithValue }) => {
  try {
    const response = await getAdminGuardianById(guardianId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to fetch guardian.',
    });
  }
});

/**
 * Fetch institutes linked to a guardian
 */
export const fetchAdminGuardianInstitutes = createAsyncThunk<
  IGetAdminGuardianInstitutesResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-guardian/fetch-institutes', async (guardianId, { rejectWithValue }) => {
  try {
    const response = await getAdminGuardianInstitutes(guardianId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to fetch guardian institutes.',
    });
  }
});

/**
 * Update an admin guardian profile
 */
export const updateAdminGuardianProfile = createAsyncThunk<
  IUpdateAdminGuardianResponse,
  { guardianId: string; data: IUpdateAdminGuardianPayload },
  { rejectValue: IErrorResponse }
>('admin-guardian/update-profile', async ({ guardianId, data }, { rejectWithValue }) => {
  try {
    const response = await updateAdminGuardian(guardianId, data);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to update guardian profile.',
    });
  }
});

/**
 * Deactivate an admin guardian
 */
export const deactivateGuardian = createAsyncThunk<
  IDeactivateGuardianResponse,
  { guardianId: string; payload: IDeactivateGuardianPayload },
  { rejectValue: IErrorResponse }
>('admin-guardian/deactivate', async ({ guardianId, payload }, { rejectWithValue }) => {
  try {
    const response = await deactivateAdminGuardian(guardianId, payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to deactivate guardian.',
    });
  }
});

/**
 * Reactivate an admin guardian
 */
export const reactivateGuardian = createAsyncThunk<
  IDeactivateGuardianResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-guardian/reactivate', async (guardianId, { rejectWithValue }) => {
  try {
    const response = await reactivateAdminGuardian(guardianId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to reactivate guardian.',
    });
  }
});

/**
 * Delete a guardian permanently
 */
export const deleteGuardian = createAsyncThunk<
  { status: number; message: string },
  string,
  { rejectValue: IErrorResponse }
>('admin-guardian/delete', async (guardianId, { rejectWithValue, dispatch }) => {
  try {
    const response = await deleteAdminGuardian(guardianId);

    dispatch(
      showNotification({
        severity: 'success',
        message: tr(
          'fe_classveew_admin.guardian_permanently_deleted',
          'Guardian has been permanently deleted.',
        ),
      }),
    );

    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);

    dispatch(
      showNotification({
        severity: 'error',
        message: Array.isArray(errorData?.message)
          ? errorData.message.join(', ')
          : errorData?.message || 'Failed to delete guardian',
      }),
    );

    return rejectWithValue({
      status: error.response?.status || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to delete guardian',
    });
  }
});

/**
 * Fetch children hierarchy linked to a guardian
 */
export const fetchAdminGuardianChildren = createAsyncThunk<
  IGetAdminGuardianChildrenResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-guardian/fetch-children', async (guardianId, { rejectWithValue }) => {
  try {
    const response = await getAdminGuardianChildren(guardianId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to fetch guardian children.',
    });
  }
});

/**
 * Unlink a child from a guardian
 */
export const unlinkGuardianChild = createAsyncThunk<
  IUnlinkGuardianChildResponse,
  { guardianId: string; studentId: string },
  { rejectValue: IErrorResponse }
>('admin-guardian/unlink-child', async ({ guardianId, studentId }, { rejectWithValue }) => {
  try {
    const response = await unlinkAdminGuardianChild(guardianId, studentId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to unlink child.',
    });
  }
});

/**
 * Link an existing guardian to a student
 */
export const linkGuardian = createAsyncThunk<
  any,
  { guardianId: string; studentId: string; relationshipId: string },
  { rejectValue: IErrorResponse }
>('adminGuardian/linkGuardian', async (payload, { rejectWithValue }) => {
  try {
    const response = await adminLinkGuardian(payload);
    return response;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to link guardian',
      errorCode: errorData?.errorCode,
    });
  }
});

/**
 * Create an admin guardian
 */
export const createAdminGuardian = createAsyncThunk<
  ICreateAdminGuardianResponse,
  ICreateAdminGuardianPayload,
  { rejectValue: IErrorResponse }
>('admin-guardian/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await createAdminGuardianService(payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to create guardian.',
    });
  }
});

/**
 * Fetch guardian organization options
 */
export const fetchGuardianOrganizationOptions = createAsyncThunk<
  IGuardianOrganizationOption[],
  void,
  { rejectValue: IErrorResponse }
>('admin-guardian/fetch-org-options', async (_, { rejectWithValue }) => {
  try {
    const response = await adminOrganizationService.fetchOrganizations({
      status: 'APPROVED',
      pageSize: 1000,
    });
    const orgs = response.data?.data?.organizations || [];
    return orgs.map((org) => ({ label: org.name, value: org.id }));
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to fetch organizations.',
    });
  }
});

/**
 * Unlink a guardian from an institute
 */
export const unlinkGuardian = createAsyncThunk<
  IUnlinkGuardianFromInstituteResponse,
  { guardianId: string; organizationId: string; payload: IUnlinkGuardianFromInstitutePayload },
  { rejectValue: IErrorResponse }
>('admin-guardian/unlink', async ({ guardianId, organizationId, payload }, { rejectWithValue }) => {
  try {
    const response = await unlinkGuardianFromInstitute(guardianId, organizationId, payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to unlink guardian.',
    });
  }
});

/**
 * Send an invite to a guardian email
 */
export const sendInvite = createAsyncThunk<
  any,
  { email: string; studentId: string },
  { rejectValue: IErrorResponse }
>('adminGuardian/sendInvite', async (payload, { rejectWithValue }) => {
  try {
    const response = await sendGuardianInvite(payload);
    return response;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to send invite',
    });
  }
});

/**
 * Manually create a new guardian and link to student
 */
export const manualCreate = createAsyncThunk<
  any,
  IManualGuardianCreationPayload,
  { rejectValue: IErrorResponse }
>('adminGuardian/manualCreate', async (payload, { rejectWithValue }) => {
  try {
    const response = await manualCreateGuardian(payload);
    return response;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || 'Failed to create guardian',
    });
  }
});
